import { useEffect, useRef, type ComponentType } from 'react';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import NoRelatedData from '../components/keyword-related/NoRelatedData';
import RelatedKeywordsModeToggle from '../components/keyword-related/RelatedKeywordsModeToggle';
import { relatedColumnsConfig } from '@/app/KeywordResearch/config/relatedColumnsConfig';
import { Col, Row } from 'react-bootstrap';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import {
  useGetRelatedKeywordsFuncMutation,
  useGetRelatedKeywordsQuery,
  useExportTableMutation
} from '@/app/KeywordResearch/api/keywordRelatedApi';
import useDomain from '@/hooks/useDomain';
import RelatedLongKeywordsFilter from '../components/keyword-related/RelatedLongKeywordsFilter';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type AgGridWrapperProps = {
  table_name?: string;
  height: string;
  columns: unknown[];
  data?: unknown[];
  total?: number;
  fetchFunction?: unknown;
  additionalData?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

type AgGridSkeletonProps = {
  settings?: Record<string, unknown>;
  height: string;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;

const KeywordsRelated = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { domain_slug } = useDomain();
  const { pathname } = useLocation();
  useGetUserInfoQuery();
  const [getRelatedKeywords] = useGetRelatedKeywordsFuncMutation();
  const [exportTable] = useExportTableMutation();
  const access = useToolAccess(null);

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات مرتبط', href: '#' }]);
  }, [setBreadcrumbData]);

  const { data, isError, isLoading, isFetching, error, refetch } =
    useGetRelatedKeywordsQuery(
    { domain_slug, page: 1, perPage: 50 },
    { skip: access.isLoading || !access.haveAccess }
  );

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
    {
      isError: access.isError,
      error: access.error,
      refetch: access.refetch,
      isFetching: access.isFetching,
    },
  ]);

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return (
      <ToolAccessDenied access={access} tool_label="ابزار کلمات مرتبط" />
    );
  }
  if (queryError) return <DataLoadError {...queryError} />;
  if (!isLoading && data?.data?.total === 0) {
    return (
      <>
        <RelatedKeywordsModeToggle mode="auto" />
        <NoRelatedData />
      </>
    );
  }

  return (
    <>
      <RelatedKeywordsModeToggle mode="auto" />
      <Row>
        <Col xxl={12}>
          <FiltersComponent
            columns={relatedColumnsConfig}
            EndComponent={<RelatedLongKeywordsFilter />}
          />
        </Col>
        <Col>
          <div ref={gridRef}>
            {!isFetching && !access.isLoading ? (
              <TypedAgGridWrapper
                table_name={pathname.split('/').slice(1, 3).join('/')}
                settings={{
                  pagination: true,
                  fullscreen: true,
                  changeHeight: true,
                  resetGrid: false,
                  saveGrid: false,
                  export: {
                    enabled: true,
                    fetchFunction: exportTable
                  }
                }}
                height="700px"
                columns={relatedColumnsConfig}
                total={data?.data?.total}
                additionalData={{ domain_slug }}
                fetchFunction={getRelatedKeywords}
                data={data?.data?.rows}
              />
            ) : (
              <TypedAgGridSkeleton
                height="650px"
                settings={{
                  pagination: true,
                  fullscreen: true,
                  changeHeight: true,
                  resetGrid: false,
                  saveGrid: false,
                  exportBtn: false
                }}
              />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default KeywordsRelated;
